import { z } from 'zod';

import {
  type AnswerReading,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import type { ArtifactStatus } from './artifacts.ts';
import { canonicalJson } from './canonical-json.ts';
import { DomainError } from './errors.ts';
import { MAX_TEXT_INTAKE_FILES, STUDY_FILE_KINDS } from './files.ts';
import {
  type FactId,
  type MaterialsFact,
  type MaterialsShape,
  LIST_SEPARATOR,
  MAX_FACT_VALUE_CHARS,
  MAX_FORM_FIELD_LIST_ITEMS,
  MAX_MATERIALS_LABEL_CHARS,
  MAX_MATERIALS_LIST_ITEM_CHARS,
  factIdSchema,
  formFieldIdSchema,
} from './materials-shape.ts';
import {
  type StudyTypeEditability,
  type StudyTypeSelection,
  templateForSelection,
} from './study-type-selection.ts';
import { type StudyTypeId, materialsShapeFor, studyTypeIdSchema } from './study-types.ts';

/**
 * Materials: what the researcher already has, and the facts Sigora took from it.
 *
 * One document holds the inputs, the reading of them and the researcher's corrections together,
 * so the version a researcher confirms is the version that says all three. Everything here is a
 * pure decision over that document; the store executes them inside its write transaction.
 */

/**
 * Upper bounds on what a researcher may send. They are placeholder policy values: generous for
 * the material a study starts from, and small enough that what the model is asked to read and
 * what the append-only history keeps stays bounded. A list entry's and a fact value's bounds sit
 * with the Materials shape, which holds a list field's declared capacity to them.
 */
export const MAX_MATERIALS_TEXT_CHARS = 20_000;
export const MAX_MATERIALS_FIELD_CHARS = 200;
export const MAX_MATERIALS_DESCRIPTION_CHARS = 2000;
export const MAX_MATERIALS_DIFFERS_CHARS = 300;

/** The longest title Sigora may propose from the materials. */
export const MAX_PROPOSED_TITLE_CHARS = 200;

/**
 * How often the Materials step re-reads a pending reading: well below the few seconds a reading
 * takes and well above what a person would notice. A placeholder.
 */
export const MATERIALS_EXTRACTION_POLL_MS = 1000;

/**
 * A reading's wait, counted from `requested_at`: the worker gives the reading's model call what is
 * left of it, and the Materials step offers "Try again" once it has run out. The one-shot ceiling,
 * twice the Goal step's 30 s, because a reading of several PDFs can take longer than that call and
 * a slow reading that succeeds beats a retry. A placeholder.
 */
export const MATERIALS_EXTRACTION_WAIT_MS = 60_000;

/**
 * The least time the worker gives a reading's model call once the files are read, however much of
 * the wait the reading of them took: files are read under their own limits, and what was read
 * still gets a call that can answer, rather than being dropped for a retry that would read the
 * same files again. A placeholder, half the one-shot ceiling; the one-shot minimum is too short
 * for a call to answer in.
 */
export const MATERIALS_READING_MIN_CALL_MS = 30_000;

/**
 * Text that says something. Blank text is refused rather than trimmed, because a schema that
 * transformed what it parsed would store a document different from the one that was hashed.
 */
const words = (max: number) => z.string().max(max).regex(/\S/, { message: 'must not be blank' });

/**
 * A list field's entries, bounded as a whole as well as one by one: the joined list is the value
 * of the fact the field fills, so a list whose joined length exceeds a fact value's bound would
 * produce a row, and a correction's `replaced`, that the content schema refuses.
 */
const formListSchema = z
  .array(words(MAX_MATERIALS_LIST_ITEM_CHARS))
  .min(1)
  .max(MAX_FORM_FIELD_LIST_ITEMS)
  .refine((entries) => entries.join(LIST_SEPARATOR).length <= MAX_FACT_VALUE_CHARS, {
    message: `the list must join to at most ${String(MAX_FACT_VALUE_CHARS)} characters`,
  });

const formValueSchema = z.union([words(MAX_MATERIALS_FIELD_CHARS), formListSchema]);
export type MaterialsFormValue = z.infer<typeof formValueSchema>;

const formInputsSchema = z.object({
  kind: z.literal('form'),
  fields: z.record(formFieldIdSchema, formValueSchema),
});

/**
 * A stored file the inputs name, by id, with what it is to the Materials. The id is a `study_files`
 * row of the same study, which the store checks inside the write; `kind` is the row's own.
 */
export const materialsFileRefSchema = z.strictObject({
  file_id: z.uuid(),
  kind: z.enum(STUDY_FILE_KINDS),
});
export type MaterialsFileRef = z.infer<typeof materialsFileRefSchema>;

const pairedVersionSchema = z.object({
  label: words(MAX_MATERIALS_LABEL_CHARS),
  description: words(MAX_MATERIALS_DESCRIPTION_CHARS).nullable(),
  file: materialsFileRefSchema.nullable(),
});

/**
 * What the researcher added, in the intake their Study Type asks for.
 *
 * The text intake is pasted text, files, or both; each paired version is a description, a file, or
 * both. A form field left empty is absent from `fields`, not an empty string. The paired intake
 * records both versions and the differs line in the document itself, so all of it is in the
 * history, and a replaced file stays named by the version that held it.
 */
export const materialsInputsSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('text'),
    text: words(MAX_MATERIALS_TEXT_CHARS).nullable(),
    files: z.array(materialsFileRefSchema),
  }),
  formInputsSchema,
  z.object({
    kind: z.literal('paired'),
    versions: z.tuple([pairedVersionSchema, pairedVersionSchema]),
    differs: words(MAX_MATERIALS_DIFFERS_CHARS).nullable(),
  }),
]);
export type MaterialsInputs = z.infer<typeof materialsInputsSchema>;

/** The inputs of content version 1: pasted text only, and a description for each paired version. */
const pairedVersionV1Schema = z.object({
  label: words(MAX_MATERIALS_LABEL_CHARS),
  description: words(MAX_MATERIALS_DESCRIPTION_CHARS),
});

const materialsInputsV1Schema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: words(MAX_MATERIALS_TEXT_CHARS) }),
  formInputsSchema,
  z.object({
    kind: z.literal('paired'),
    versions: z.tuple([pairedVersionV1Schema, pairedVersionV1Schema]),
    differs: words(MAX_MATERIALS_DIFFERS_CHARS).nullable(),
  }),
]);

/** Every file the inputs name, in input order: the text intake's files, then each paired version's. */
export const materialsFileRefs = (inputs: MaterialsInputs): readonly MaterialsFileRef[] => {
  if (inputs.kind === 'text') {
    return inputs.files;
  }

  if (inputs.kind === 'paired') {
    return inputs.versions.flatMap((version) => (version.file === null ? [] : [version.file]));
  }

  return [];
};

/**
 * The paired version a file is the file of, by its label, or `null` when no paired version holds
 * it — which every file of a text or form intake is.
 */
export const pairedLabelOf = (inputs: MaterialsInputs, fileId: string): string | null =>
  inputs.kind === 'paired'
    ? (inputs.versions.find((version) => version.file?.file_id === fileId)?.label ?? null)
    : null;

type PairedVersion = z.infer<typeof pairedVersionSchema>;

/**
 * A description as two versions are compared on it: compatibility forms folded (NFKC), every run of
 * whitespace one space, trimmed and lower-cased.
 */
const foldedDescription = (description: string): string =>
  description.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Whether two paired versions are the same version twice: one file named by both, or no file and
 * descriptions that read alike once folded (`foldedDescription`). Two different files of the same thing
 * are not caught; the differs fact is what makes the researcher say what changes.
 */
export const samePairedVersions = ([first, second]: readonly [
  PairedVersion,
  PairedVersion,
]): boolean => {
  if (first.file !== null || second.file !== null) {
    return first.file?.file_id === second.file?.file_id;
  }

  return (
    first.description !== null &&
    second.description !== null &&
    foldedDescription(first.description) === foldedDescription(second.description)
  );
};

/** What a researcher is told when the two paired versions are the same, in the shape's labels. */
export const samePairedVersionsProblem = ([first, second]: readonly [string, string]): string =>
  `${first} and ${second} are the same; change one so there is something to compare`;

/** A fact's value: text, or `null` for "none found". */
export const factValueSchema = words(MAX_FACT_VALUE_CHARS).nullable();

const extractionRequestSchema = z.object({
  request_id: z.uuid(),
  requested_at: z.iso.datetime(),
});

/**
 * Why part of a file went unread. `image_too_large` is an image the reading could not send along
 * with the rest without passing what one request to the model may carry.
 */
export const FILE_COVERAGE_REASONS = [
  'password_protected',
  'damaged',
  'no_text',
  'page_limit',
  'text_limit',
  'image_too_large',
] as const;
export type FileCoverageReason = (typeof FILE_COVERAGE_REASONS)[number];

/** A run of pages, 1-based and inclusive at both ends. */
const pageRangeSchema = z.tuple([z.int().positive(), z.int().positive()]);

/**
 * How much of one file a reading read, which is what lets the screen say "read pages 1–4 of 9"
 * and lets a fact the reading did not find say it was not in what was read, rather than that
 * there is none.
 *
 * `pages` is set for a PDF whose pages could be counted, with the page runs that were read in
 * full, in order, each separated from the next by at least one unread page; `characters` for a
 * text file. A PDF page that alone took the reading past its characters budget is cut there and
 * named in `pages.part`, with the characters read of it and the characters it holds: it comes
 * after every run, is in none of them, and carries `text_limit`, so a page read in part is never
 * counted as read. An image, and a file that could not be opened at all, have neither count.
 * When a file is counted the outcome is what the counts say: `read` exactly when every page or
 * character was read, `unread` exactly when nothing of it was, and `partial` otherwise, so a
 * reading can never call unread text read. `read` also holds exactly when no reason is given.
 */
export const fileCoverageSchema = z
  .object({
    file_id: z.uuid(),
    outcome: z.enum(['read', 'partial', 'unread']),
    reasons: z.array(z.enum(FILE_COVERAGE_REASONS)),
    pages: z
      .object({
        total: z.int().positive(),
        read: z.array(pageRangeSchema),
        part: z
          .object({
            page: z.int().positive(),
            characters_read: z.int().positive(),
            characters_total: z.int().positive(),
          })
          .optional(),
      })
      .nullable(),
    characters: z.object({ total: z.int().positive(), read: z.int().nonnegative() }).nullable(),
  })
  .superRefine((coverage, context) => {
    const problem = (message: string) => context.addIssue({ code: 'custom', message });
    const { outcome, reasons, pages, characters } = coverage;

    if (new Set(reasons).size !== reasons.length) {
      problem('a reason is named twice');
    }

    if ((outcome === 'read') !== (reasons.length === 0)) {
      problem('a file is read in full exactly when nothing kept any of it unread');
    }

    if (pages !== null && characters !== null) {
      problem('a file is counted in pages or in characters, not both');
    }

    if (pages !== null) {
      // Page 0 does not exist, so the first run may start at page 1.
      let previous = -1;
      for (const [first, last] of pages.read) {
        if (first <= previous + 1 || last < first || last > pages.total) {
          problem(
            `page runs must be in order, separated by unread pages, and within ${String(pages.total)} pages`,
          );
          break;
        }

        previous = last;
      }

      const { part } = pages;
      if (part !== undefined) {
        if (part.page <= previous || part.page > pages.total) {
          problem('a page read in part comes after every page read in full, within the file');
        }

        if (part.characters_read >= part.characters_total) {
          problem('a page read in part has characters left unread');
        }

        if (!reasons.includes('text_limit')) {
          problem('a page is read in part only when the reading ran out of characters');
        }
      }
    }

    if (characters !== null && characters.read > characters.total) {
      problem('more characters read than the file holds');
    }

    const counted = pages !== null || characters !== null;
    const nothingRead =
      (pages !== null && pages.read.length === 0 && pages.part === undefined) ||
      (characters !== null && characters.read === 0);
    if (counted && (outcome === 'unread') !== nothingRead) {
      problem('a file is unread exactly when none of it was read');
    }

    const [onlyRun] = pages?.read ?? [];
    const everythingRead =
      (pages !== null &&
        pages.part === undefined &&
        pages.read.length === 1 &&
        onlyRun?.[0] === 1 &&
        onlyRun[1] === pages.total) ||
      (characters !== null && characters.read === characters.total);
    if (counted && (outcome === 'read') !== everythingRead) {
      problem('a file is read exactly when every page or character of it was read');
    }

    if (!counted && outcome === 'partial') {
      problem('only a file counted in pages or characters can be read in part');
    }
  });
export type FileCoverage = z.infer<typeof fileCoverageSchema>;

const readyExtractionFields = {
  status: z.literal('ready'),
  values: z.record(factIdSchema, factValueSchema),
  proposed_title: words(MAX_PROPOSED_TITLE_CHARS).nullable(),
  changed_facts: z.array(factIdSchema),
};

/**
 * The reading of the inputs this version holds.
 *
 * - `pending` — the worker has not read them yet.
 * - `ready` — `values` holds one entry per fact the model was asked for, `null` where it found
 *   none. `changed_facts` names the facts whose correction this reading overrode. `coverage` says
 *   how much of each file input was read, in input order.
 * - `unreadable` — the model answered, and the answer did not parse.
 * - `unavailable` — no answer could be had: no model, no queue, the day's budget spent, or the
 *   call failed.
 *
 * A fact an input fills is never in `values`: it is read off the inputs, so the model is never
 * asked for it.
 */
export const materialsExtractionSchema = z.discriminatedUnion('status', [
  extractionRequestSchema.extend({ status: z.literal('pending') }),
  extractionRequestSchema.extend({
    ...readyExtractionFields,
    coverage: z.array(fileCoverageSchema),
  }),
  extractionRequestSchema.extend({ status: z.literal('unreadable') }),
  extractionRequestSchema.extend({ status: z.literal('unavailable') }),
]);
export type MaterialsExtraction = z.infer<typeof materialsExtractionSchema>;
export type MaterialsExtractionStatus = MaterialsExtraction['status'];

const materialsExtractionV1Schema = z.discriminatedUnion('status', [
  extractionRequestSchema.extend({ status: z.literal('pending') }),
  extractionRequestSchema.extend(readyExtractionFields),
  extractionRequestSchema.extend({ status: z.literal('unreadable') }),
  extractionRequestSchema.extend({ status: z.literal('unavailable') }),
]);

/**
 * A researcher's fix to one row.
 *
 * `replaced` is the value Sigora had for the fact when the fix was made, which is what a later
 * reading is compared against. It is absent when nothing had been read for the fact yet.
 */
export const materialsCorrectionSchema = z.object({
  value: factValueSchema,
  replaced: factValueSchema.optional(),
});
export type MaterialsCorrection = z.infer<typeof materialsCorrectionSchema>;

const studyTypeCaptureSchema = z.object({
  id: studyTypeIdSchema,
  template_version: z.int().positive(),
});

/**
 * One version of a study's Materials, as the artifact store holds it.
 *
 * `study_type` is the type and template version the inputs were captured against, which decides
 * the intake and the facts; a study whose type has moved since has Materials that are not current
 * (`materialsConfirmedCurrent`).
 *
 * `stimulus_candidates` names the images the researcher said participants may see, each an image
 * the inputs hold. It sits beside the inputs rather than in them, so choosing one changes no input
 * and starts no reading, and it is versioned with the Materials it was chosen from.
 *
 * The inputs name each file once, and a ready reading covers exactly the files they name, one entry
 * each in input order, so no file can go unmentioned; an image is covered whole or not at all.
 */
export const materialsContentSchema = z
  .object({
    study_type: studyTypeCaptureSchema,
    inputs: materialsInputsSchema,
    extraction: materialsExtractionSchema,
    corrections: z.record(factIdSchema, materialsCorrectionSchema),
    stimulus_candidates: z.array(z.uuid()),
  })
  .superRefine((content, context) => {
    const held = materialsFileRefs(content.inputs);
    const refs = new Map(held.map((ref) => [ref.file_id, ref.kind]));
    const candidates = content.stimulus_candidates;

    if (refs.size !== held.length) {
      context.addIssue({ code: 'custom', message: 'the inputs name a file twice' });
    }

    if (new Set(candidates).size !== candidates.length) {
      context.addIssue({ code: 'custom', message: 'a Stimulus candidate is named twice' });
    }

    for (const fileId of candidates) {
      if (refs.get(fileId) !== 'image') {
        context.addIssue({
          code: 'custom',
          message: `Stimulus candidate ${fileId} is not an image these inputs hold`,
        });
      }
    }

    if (content.extraction.status === 'ready') {
      const { coverage } = content.extraction;
      const covered = coverage.map((entry) => entry.file_id);
      if (canonicalJson(covered) !== canonicalJson(held.map((ref) => ref.file_id))) {
        context.addIssue({
          code: 'custom',
          message: 'a ready reading covers every file these inputs hold, once each, in input order',
        });
      }

      for (const entry of coverage) {
        if (
          refs.get(entry.file_id) === 'image' &&
          (entry.pages !== null || entry.characters !== null)
        ) {
          context.addIssue({
            code: 'custom',
            message: `image ${entry.file_id} is read whole or not at all, never counted in pages or characters`,
          });
        }
      }
    }
  });
export type MaterialsContent = z.infer<typeof materialsContentSchema>;

/** Materials content version 1, read and lifted to the current shape by `liftMaterialsV1`. */
export const materialsContentV1Schema = z.object({
  study_type: studyTypeCaptureSchema,
  inputs: materialsInputsV1Schema,
  extraction: materialsExtractionV1Schema,
  corrections: z.record(factIdSchema, materialsCorrectionSchema),
});
export type MaterialsContentV1 = z.infer<typeof materialsContentV1Schema>;

/**
 * A version 1 document as the current shape: it names no files, read none of them and chose no
 * Stimulus, so every consumer reads one shape whichever version a row was written at.
 */
export const liftMaterialsV1 = (content: MaterialsContentV1): MaterialsContent => {
  const { inputs, extraction } = content;

  return {
    study_type: content.study_type,
    inputs:
      inputs.kind === 'text'
        ? { kind: 'text', text: inputs.text, files: [] }
        : inputs.kind === 'paired'
          ? {
              kind: 'paired',
              versions: [
                { ...inputs.versions[0], file: null },
                { ...inputs.versions[1], file: null },
              ],
              differs: inputs.differs,
            }
          : inputs,
    extraction: extraction.status === 'ready' ? { ...extraction, coverage: [] } : extraction,
    corrections: content.corrections,
    stimulus_candidates: [],
  };
};

const sameStudyType = (
  captured: MaterialsContent['study_type'],
  selection: { id: StudyTypeId; templateVersion: number },
): boolean =>
  captured.id === selection.id && captured.template_version === selection.templateVersion;

/** The Materials shape a version was captured against. */
export const materialsShapeOf = (content: MaterialsContent): MaterialsShape =>
  materialsShapeFor(
    templateForSelection({
      id: content.study_type.id,
      category: null,
      templateVersion: content.study_type.template_version,
    }),
  );

const invalid = (problems: readonly string[]): DomainError =>
  new DomainError('invalid_content', 'invalid materials', { kind: 'materials', problems });

/**
 * Everything wrong with a set of inputs for a shape, or nothing.
 *
 * The schema bounds each value; this is what only the shape knows: the intake kind, which fields
 * exist, which are lists and how long, which are required, and the paired labels. Returned as a
 * list rather than thrown so a screen can check what it is about to send against the same rule.
 */
export const materialsInputsProblems = (
  shape: MaterialsShape,
  inputs: MaterialsInputs,
): readonly string[] => {
  const { intake } = shape;
  if (intake.kind !== inputs.kind) {
    return [`this study takes ${intake.kind} materials, not ${inputs.kind}`];
  }

  const fileIds = materialsFileRefs(inputs).map((ref) => ref.file_id);
  const fileProblems =
    new Set(fileIds).size === fileIds.length ? [] : ['the same file is added twice'];

  if (inputs.kind === 'text') {
    return [
      ...(inputs.text === null && inputs.files.length === 0 ? ['add some text or a file'] : []),
      ...(inputs.files.length > MAX_TEXT_INTAKE_FILES
        ? [`add at most ${String(MAX_TEXT_INTAKE_FILES)} files`]
        : []),
      ...fileProblems,
    ];
  }

  if (intake.kind === 'paired' && inputs.kind === 'paired') {
    return [
      ...inputs.versions.flatMap((version, index) => [
        ...(version.label === intake.labels[index]
          ? []
          : [`version ${String(index + 1)} is labelled ${intake.labels[index] ?? ''}`]),
        ...(version.description === null && version.file === null
          ? [
              `${intake.labels[index] ?? `version ${String(index + 1)}`} needs a description or a file`,
            ]
          : []),
      ]),
      // A file named twice here is both versions naming it, which is this same refusal.
      ...(samePairedVersions(inputs.versions) ? [samePairedVersionsProblem(intake.labels)] : []),
    ];
  }

  if (intake.kind !== 'form' || inputs.kind !== 'form') {
    return [];
  }

  const problems: string[] = [];
  const fields = new Map(intake.fields.map((field) => [field.id, field]));

  for (const [id, value] of Object.entries(inputs.fields)) {
    const field = fields.get(id);
    if (field === undefined) {
      problems.push(`no field ${id}`);
      continue;
    }

    if (field.maxItems === undefined && Array.isArray(value)) {
      problems.push(`${id} takes one value`);
    }

    if (field.maxItems !== undefined && !Array.isArray(value)) {
      problems.push(`${id} takes a list`);
    }

    if (field.maxItems !== undefined && Array.isArray(value) && value.length > field.maxItems) {
      problems.push(`${id} takes at most ${String(field.maxItems)}`);
    }
  }

  for (const field of intake.fields) {
    if (field.required && !Object.hasOwn(inputs.fields, field.id)) {
      problems.push(`${field.id} is required`);
    }
  }

  if (Object.keys(inputs.fields).length === 0) {
    problems.push('the form is empty');
  }

  return problems;
};

/** The same rule, refusing inputs that break it. */
export const inputsFitShape = (shape: MaterialsShape, inputs: MaterialsInputs): MaterialsInputs => {
  const problems = materialsInputsProblems(shape, inputs);
  if (problems.length > 0) {
    throw invalid(problems);
  }

  return inputs;
};

/**
 * The facts the inputs themselves fill, and with what.
 *
 * A form field fills its fact whether or not it was filled in, with `null` for an empty one: the
 * researcher said what they had, and asking a model to find a brand in a form that left the brand
 * blank would be asking it to guess. The paired differs line fills its fact only when it is given,
 * and otherwise the fact is read from the two descriptions.
 */
const factsFilledByInputs = (
  shape: MaterialsShape,
  inputs: MaterialsInputs,
): ReadonlyMap<FactId, string | null> => {
  const filled = new Map<FactId, string | null>();
  const { intake } = shape;

  if (intake.kind === 'form' && inputs.kind === 'form') {
    for (const field of intake.fields) {
      if (field.fillsFact === undefined) {
        continue;
      }

      const value = Object.hasOwn(inputs.fields, field.id) ? inputs.fields[field.id] : undefined;
      filled.set(
        field.fillsFact,
        value === undefined ? null : Array.isArray(value) ? value.join(LIST_SEPARATOR) : value,
      );
    }
  }

  if (
    intake.kind === 'paired' &&
    inputs.kind === 'paired' &&
    intake.differsFact !== undefined &&
    inputs.differs !== null
  ) {
    filled.set(intake.differsFact, inputs.differs);
  }

  return filled;
};

/** The facts the model is asked for: every fact no input fills, in row order. */
export const factsToExtract = (
  shape: MaterialsShape,
  inputs: MaterialsInputs,
): readonly MaterialsFact[] => {
  const filled = factsFilledByInputs(shape, inputs);

  return shape.facts.filter((fact) => !filled.has(fact.id));
};

/**
 * What Sigora has for a fact before any correction: the input's value where an input fills it,
 * otherwise the reading's, or `undefined` while nothing has been read.
 */
const readValue = (
  filled: ReadonlyMap<FactId, string | null>,
  extraction: MaterialsExtraction,
  factId: FactId,
): string | null | undefined => {
  if (filled.has(factId)) {
    return filled.get(factId) ?? null;
  }

  if (extraction.status === 'ready' && Object.hasOwn(extraction.values, factId)) {
    return extraction.values[factId] ?? null;
  }

  return undefined;
};

/**
 * Where a row's value came from: an input that fills the fact (a form field, or the paired
 * differs line), the model's reading, or the researcher's fix.
 */
export type MaterialsRowSource = 'input' | 'extracted' | 'user';

/**
 * - `found` / `none_found` — the row has a value, or says there is none.
 * - `none_found_in_read` — the reading found no value, and part of a file went unread, so all the
 *   row can say is that the value is not in what was read.
 * - `not_read` — nothing has been read for the fact yet and nobody has filled it in.
 * - `changed` — a new reading of changed inputs overrode the researcher's fix to this row.
 */
export type MaterialsRowState =
  'found' | 'none_found' | 'none_found_in_read' | 'not_read' | 'changed';

/** Whether a settled reading left any part of any file unread. */
const readingIncomplete = (extraction: MaterialsExtraction): boolean =>
  extraction.status === 'ready' &&
  extraction.coverage.some((coverage) => coverage.outcome !== 'read');

export interface MaterialsRow {
  factId: FactId;
  label: string;
  required: boolean;
  value: string | null;
  source: MaterialsRowSource;
  state: MaterialsRowState;
}

/**
 * The rows the Materials screen shows, one per fact in the shape's order.
 *
 * A correction wins over everything. A row a new reading changed says so next, whether an input
 * or the model supplied the value that overrode the fix. Then an input that fills the fact, then
 * the reading. Corrections naming a fact the shape does not have are not rows.
 */
export const materialsRows = (content: MaterialsContent): readonly MaterialsRow[] => {
  const shape = materialsShapeOf(content);
  const filled = factsFilledByInputs(shape, content.inputs);
  const { extraction, corrections } = content;
  const changed = new Set(extraction.status === 'ready' ? extraction.changed_facts : []);
  const incomplete = readingIncomplete(extraction);

  return shape.facts.map((fact): MaterialsRow => {
    const base = { factId: fact.id, label: fact.label, required: fact.required };
    const valued = (value: string | null, source: MaterialsRowSource): MaterialsRow => ({
      ...base,
      value,
      source,
      state:
        value !== null
          ? 'found'
          : source === 'extracted' && incomplete
            ? 'none_found_in_read'
            : 'none_found',
    });

    if (Object.hasOwn(corrections, fact.id)) {
      return valued(corrections[fact.id]?.value ?? null, 'user');
    }

    const source: MaterialsRowSource = filled.has(fact.id) ? 'input' : 'extracted';
    const read = readValue(filled, extraction, fact.id);
    if (read === undefined) {
      return { ...base, value: null, source, state: 'not_read' };
    }

    return changed.has(fact.id)
      ? { ...base, value: read, source, state: 'changed' }
      : valued(read, source);
  });
};

/** The required rows with no value, which is what blocks a confirmation. */
export const missingRequiredFacts = (content: MaterialsContent): readonly MaterialsRow[] =>
  materialsRows(content).filter((row) => row.required && row.value === null);

/** The request a missing required fact is answered with, rather than a value nobody gave. */
export const missingFactRequest = (label: string): string =>
  `Sigora couldn't find ${label}. Add it to your materials or fix the row.`;

export type MaterialsConfirmability =
  | { confirmable: true }
  | { confirmable: false; reason: 'extraction_pending' }
  | { confirmable: false; reason: 'missing_required'; missing: readonly MaterialsRow[] };

/**
 * Whether this version may be confirmed (the Materials step's Continue).
 *
 * Not while the reading is pending, because the rows are about to change under the confirmation.
 * An unreadable or unavailable reading does not block it: the researcher may fill the rows in
 * themselves. A required fact with no value always does.
 */
export const materialsConfirmable = (content: MaterialsContent): MaterialsConfirmability => {
  if (content.extraction.status === 'pending') {
    return { confirmable: false, reason: 'extraction_pending' };
  }

  const missing = missingRequiredFacts(content);

  return missing.length > 0
    ? { confirmable: false, reason: 'missing_required', missing }
    : { confirmable: true };
};

/** The same decision, refusing a confirmation it does not allow. */
export const assertMaterialsConfirmable = (content: MaterialsContent): void => {
  const decision = materialsConfirmable(content);
  if (decision.confirmable) {
    return;
  }

  if (decision.reason === 'extraction_pending') {
    throw new DomainError(
      'materials_extraction_pending',
      'Sigora is still reading your materials. Check them again once it has.',
      { request_id: content.extraction.request_id },
    );
  }

  throw new DomainError(
    'materials_missing_required',
    decision.missing.map((row) => missingFactRequest(row.label)).join(' '),
    { fact_ids: decision.missing.map((row) => row.factId) },
  );
};

/**
 * Whether a study's Materials may change: saved, fixed or confirmed.
 *
 * On a draft or a ready study that nobody has locked, launched or paid for, as the Study Type may.
 * A ready study can change because readiness reads the Materials: a change makes a version nobody
 * has confirmed, which takes the study back to draft in the same transaction, so no study that can
 * be bought ever has unconfirmed Materials behind it. Confirming them on a ready study can only
 * restate the confirmation readiness already requires. A purchase hold is refused beside this, by
 * the store.
 */
export const isMaterialsEditable = ({
  state,
  lockedAt,
  launchedAt,
  paidAt,
}: StudyTypeEditability): boolean =>
  (state === 'draft' || state === 'ready') &&
  lockedAt === null &&
  launchedAt === null &&
  paidAt === null;

export const assertMaterialsEditable = (study: StudyTypeEditability): void => {
  if (!isMaterialsEditable(study)) {
    throw new DomainError(
      'materials_not_editable',
      'Materials can change only until the study is paid for, frozen or launched.',
      {
        state: study.state,
        locked_at: study.lockedAt?.toISOString() ?? null,
        launched_at: study.launchedAt?.toISOString() ?? null,
        paid_at: study.paidAt?.toISOString() ?? null,
      },
    );
  }
};

/**
 * Whether a study's Materials are confirmed at the version it holds now, for the Study Type it
 * has now.
 *
 * The one authority on confirmation: the step's completion, the digest and anything generated from
 * the facts ask this and nothing else, so a confirmation that has been overtaken by an edit or by a
 * change of type authorizes nothing. `content` is the current version's.
 */
export const materialsConfirmedCurrent = (
  state: { status: ArtifactStatus; currentVersion: number; approvedVersion: number | null } | null,
  content: MaterialsContent | null,
  selection: StudyTypeSelection | null,
): boolean =>
  state !== null &&
  content !== null &&
  selection !== null &&
  state.status === 'approved' &&
  state.approvedVersion === state.currentVersion &&
  sameStudyType(content.study_type, selection);

export interface MaterialsFactValue {
  factId: FactId;
  label: string;
  value: string | null;
  /** True for a `null` the reading could not find in the parts of the files it read. */
  incompleteRead: boolean;
}

/**
 * The facts a version holds, in row order, "none found" included.
 *
 * It does not decide whether they are confirmed; a caller asks `materialsConfirmedCurrent` first
 * and reads the facts only when it holds.
 */
export const confirmedMaterialsFacts = (content: MaterialsContent): readonly MaterialsFactValue[] =>
  materialsRows(content).map((row) => ({
    factId: row.factId,
    label: row.label,
    value: row.value,
    incompleteRead: row.state === 'none_found_in_read',
  }));

/**
 * Whether a save made over a version that has since moved is the same save sent again — a retry
 * whose first response was lost — rather than a conflict: the Materials as they stand hold exactly
 * these inputs for the same Study Type. Such a save answers the version that is there and writes
 * nothing, as a reworded question retried after a lost response does.
 */
export const isMaterialsSaveRetry = ({
  studyType,
  current,
  inputs,
}: {
  studyType: StudyTypeSelection;
  current: MaterialsContent;
  inputs: MaterialsInputs;
}): boolean =>
  sameStudyType(current.study_type, studyType) &&
  canonicalJson(current.inputs) === canonicalJson(inputs);

export type MaterialsSavePlan =
  /** The inputs are the ones already read: nothing is written and no job is sent. */
  | { kind: 'unchanged'; content: MaterialsContent }
  | { kind: 'write'; content: MaterialsContent; enqueue: boolean };

/**
 * What saving a set of inputs does.
 *
 * - Identical inputs whose reading is `ready` change nothing, which makes a repeat save free.
 *   With a queue, identical inputs in any other state start a new request, which makes a re-save
 *   the retry for a reading that is pending past its deadline, unreadable or unavailable. Without
 *   one, identical inputs whose reading is unreadable or unavailable change nothing either: the
 *   only request it could write is another unavailable one, so a repeat save would retry nothing
 *   and only move the version past a confirmation the researcher made by filling the rows in.
 * - Otherwise a new version with a new request is written, and the corrections carry forward when
 *   the Study Type and template version are the ones they were made against. A new request
 *   supersedes whatever reading was running: its settle names a request that is no longer current.
 * - Inputs that fill every fact are settled `ready` in the same write, with no job.
 * - The Stimulus candidates carry forward while they are still images the new inputs hold, so a
 *   replaced image is not a candidate until it is chosen again.
 * - With no queue to send the job to, the request is written `unavailable` rather than left
 *   pending for a worker that will never see it.
 */
export const planMaterialsSave = ({
  studyType,
  current,
  inputs,
  requestId,
  requestedAt,
  canEnqueue,
}: {
  studyType: StudyTypeSelection;
  current: MaterialsContent | null;
  inputs: MaterialsInputs;
  requestId: string;
  requestedAt: string;
  canEnqueue: boolean;
}): MaterialsSavePlan => {
  const shape = materialsShapeFor(templateForSelection(studyType));
  inputsFitShape(shape, inputs);

  const sameType = current !== null && sameStudyType(current.study_type, studyType);
  if (
    sameType &&
    canonicalJson(current.inputs) === canonicalJson(inputs) &&
    (current.extraction.status === 'ready' ||
      (!canEnqueue && current.extraction.status !== 'pending'))
  ) {
    return { kind: 'unchanged', content: current };
  }

  const request = { request_id: requestId, requested_at: requestedAt };
  const images = new Set(
    materialsFileRefs(inputs)
      .filter((ref) => ref.kind === 'image')
      .map((ref) => ref.file_id),
  );
  const pending: MaterialsContent = {
    study_type: { id: studyType.id, template_version: studyType.templateVersion },
    inputs,
    extraction: { ...request, status: 'pending' },
    corrections: sameType ? current.corrections : {},
    stimulus_candidates: (current?.stimulus_candidates ?? []).filter((fileId) =>
      images.has(fileId),
    ),
  };

  if (factsToExtract(shape, inputs).length === 0) {
    return {
      kind: 'write',
      content: settleReading(shape, pending, { values: {}, proposedTitle: null }, []),
      enqueue: false,
    };
  }

  return canEnqueue
    ? { kind: 'write', content: pending, enqueue: true }
    : {
        kind: 'write',
        content: { ...pending, extraction: { ...request, status: 'unavailable' } },
        enqueue: false,
      };
};

export type MaterialsExtractionOutcome =
  | {
      status: 'ready';
      values: Readonly<Record<FactId, string | null>>;
      proposedTitle: string | null;
    }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * A `ready` reading applied to a pending version, with the corrections merged against it.
 *
 * A correction whose `replaced` value is what the new reading says is unaffected: the inputs still
 * say what they said when the researcher fixed the row, so the fix stands. One whose `replaced`
 * value differs is affected: the new inputs now speak to that fact, so the reading wins, the row is
 * marked changed, and the fix stays readable in the version history. A correction made before
 * anything was read has nothing to be compared against and stands.
 */
const settleReading = (
  shape: MaterialsShape,
  pending: MaterialsContent,
  reading: { values: Readonly<Record<FactId, string | null>>; proposedTitle: string | null },
  coverage: readonly FileCoverage[],
): MaterialsContent => {
  const asked = factsToExtract(shape, pending.inputs).map((fact) => fact.id);
  const answered = Object.keys(reading.values);
  if (
    answered.length !== asked.length ||
    !asked.every((factId) => Object.hasOwn(reading.values, factId))
  ) {
    throw invalid([`a reading must answer exactly ${asked.join(', ')}`]);
  }

  const extraction: MaterialsExtraction = {
    request_id: pending.extraction.request_id,
    requested_at: pending.extraction.requested_at,
    status: 'ready',
    values: { ...reading.values },
    proposed_title: reading.proposedTitle,
    changed_facts: [],
    coverage: [...coverage],
  };
  const filled = factsFilledByInputs(shape, pending.inputs);
  const corrections: Record<FactId, MaterialsCorrection> = {};
  const changed = new Set<FactId>();

  for (const [factId, correction] of Object.entries(pending.corrections)) {
    if (!shape.facts.some((fact) => fact.id === factId)) {
      continue;
    }

    if (
      correction.replaced === undefined ||
      readValue(filled, extraction, factId) === correction.replaced
    ) {
      corrections[factId] = correction;
    } else {
      changed.add(factId);
    }
  }

  return {
    ...pending,
    extraction: {
      ...extraction,
      changed_facts: shape.facts.map((fact) => fact.id).filter((factId) => changed.has(factId)),
    },
    corrections,
  };
};

/**
 * What a reading's outcome does to the current version, or `null` where it does nothing.
 *
 * It settles only the request it was sent for, and only while that request is pending: a save
 * that landed while the worker was reading has written a new request, so the old reading's
 * settle finds a request id that is no longer current and writes nothing. That is what makes a
 * superseded job free rather than a race.
 *
 * `coverage` is how much of each file input the reading read, in input order, and is kept only on
 * a `ready` reading.
 */
export const settleExtraction = (
  current: MaterialsContent,
  requestId: string,
  outcome: MaterialsExtractionOutcome,
  coverage: readonly FileCoverage[] = [],
): MaterialsContent | null => {
  if (current.extraction.status !== 'pending' || current.extraction.request_id !== requestId) {
    return null;
  }

  if (outcome.status !== 'ready') {
    return {
      ...current,
      extraction: {
        request_id: current.extraction.request_id,
        requested_at: current.extraction.requested_at,
        status: outcome.status,
      },
    };
  }

  return settleReading(materialsShapeOf(current), current, outcome, coverage);
};

/** The images the researcher said participants may see, in the order they were chosen. */
export const stimulusCandidates = (content: MaterialsContent): readonly string[] =>
  content.stimulus_candidates;

/** One fact whose value two Materials versions disagree on; `null` is "none". */
export interface MaterialsFactChange {
  factId: FactId;
  label: string;
  from: string | null;
  to: string | null;
}

/**
 * What changed between two Materials versions, as something reconciling a document built on them
 * reads it. Files are named by id, and each list is disjoint from the others.
 */
export interface MaterialsChanges {
  /** Every fact whose value differs, in the later version's row order, then any it no longer has. */
  facts: readonly MaterialsFactChange[];
  /** Files the later version names and the earlier one did not. */
  filesAdded: readonly string[];
  /** Files the earlier version named and the later one does not. */
  filesRemoved: readonly string[];
  /** Images both versions hold that the earlier marked to show and the later does not. */
  imagesUnmarked: readonly string[];
  /** Images the later version marks to show that the earlier one did not. */
  imagesMarked: readonly string[];
}

/**
 * What changed between two versions of a study's Materials: each fact whose value moved, by id and
 * label, the files added and removed, and the images marked or unmarked to show. A fact a later
 * Study Type's shape no longer has reads as changed to none.
 */
export const materialsChanges = (
  before: MaterialsContent,
  after: MaterialsContent,
): MaterialsChanges => {
  const earlier = new Map(materialsRows(before).map((row) => [row.factId, row]));
  const later = materialsRows(after);
  const facts: MaterialsFactChange[] = [];
  for (const row of later) {
    const from = earlier.get(row.factId)?.value ?? null;
    if (from !== row.value) {
      facts.push({ factId: row.factId, label: row.label, from, to: row.value });
    }
  }

  const kept = new Set(later.map((row) => row.factId));
  for (const row of earlier.values()) {
    if (!kept.has(row.factId) && row.value !== null) {
      facts.push({ factId: row.factId, label: row.label, from: row.value, to: null });
    }
  }

  const filesBefore = materialsFileRefs(before.inputs).map((ref) => ref.file_id);
  const filesAfter = materialsFileRefs(after.inputs).map((ref) => ref.file_id);
  const markedBefore = new Set(before.stimulus_candidates);
  const markedAfter = new Set(after.stimulus_candidates);

  return {
    facts,
    filesAdded: filesAfter.filter((id) => !filesBefore.includes(id)),
    filesRemoved: filesBefore.filter((id) => !filesAfter.includes(id)),
    imagesUnmarked: [...markedBefore].filter(
      (id) => !markedAfter.has(id) && filesAfter.includes(id),
    ),
    imagesMarked: [...markedAfter].filter((id) => !markedBefore.has(id)),
  };
};

/**
 * One image offered to participants, or taken back, as the next version of the Materials — or
 * `null` when it is already so, which writes nothing.
 *
 * It changes the candidates and nothing else: the inputs, the reading and its request, and the
 * corrections are kept, so no new reading starts. Only an image the current inputs hold can be a
 * candidate.
 */
export const planStimulusChange = (
  content: MaterialsContent,
  fileId: string,
  candidate: boolean,
): MaterialsContent | null => {
  const ref = materialsFileRefs(content.inputs).find((held) => held.file_id === fileId);
  if (ref === undefined) {
    throw new DomainError(
      'materials_file_not_in_inputs',
      'That file is not in these materials. Add it to your materials first.',
      { file_id: fileId },
    );
  }

  if (ref.kind !== 'image') {
    throw new DomainError(
      'materials_file_not_image',
      'Only an image can be shown to participants.',
      { file_id: fileId },
    );
  }

  const chosen = content.stimulus_candidates.includes(fileId);
  if (chosen === candidate) {
    return null;
  }

  return {
    ...content,
    stimulus_candidates: candidate
      ? [...content.stimulus_candidates, fileId]
      : content.stimulus_candidates.filter((held) => held !== fileId),
  };
};

/**
 * One row fixed, applied to the current version.
 *
 * It touches that row's correction and nothing else, so a fix cannot revert a neighbouring fix or
 * a reading that settled while the researcher was typing: the store applies it to whatever version
 * is current inside its write transaction. A fix back to what Sigora read is no correction at all,
 * and fixing a row acknowledges that a new reading changed it.
 */
export const applyFactCorrection = (
  current: MaterialsContent,
  factId: string,
  value: string | null,
): MaterialsContent => {
  const shape = materialsShapeOf(current);
  if (!shape.facts.some((fact) => fact.id === factId)) {
    throw new DomainError('materials_fact_unknown', `no fact ${factId} in these materials`, {
      fact_id: factId,
    });
  }

  const parsed = factValueSchema.safeParse(value);
  if (!parsed.success) {
    throw invalid([`${factId}: ${z.prettifyError(parsed.error)}`]);
  }

  const read = readValue(factsFilledByInputs(shape, current.inputs), current.extraction, factId);
  const corrections = Object.fromEntries(
    Object.entries(current.corrections).filter(([corrected]) => corrected !== factId),
  );
  if (read === undefined) {
    corrections[factId] = { value: parsed.data };
  } else if (read !== parsed.data) {
    corrections[factId] = { value: parsed.data, replaced: read };
  }

  const { extraction } = current;

  return {
    ...current,
    extraction:
      extraction.status === 'ready'
        ? {
            ...extraction,
            changed_facts: extraction.changed_facts.filter((changed) => changed !== factId),
          }
        : extraction,
    corrections,
  };
};

/**
 * The queue Materials are read on. The API process stores the request and enqueues it; the worker,
 * which is the only process holding a model, answers it.
 */
export const QUEUE_MATERIALS_EXTRACT = 'materials.extract';

/**
 * The job the API process sends. It names the request as well as the study, so a job for inputs
 * the researcher has since replaced is recognised as stale and spends nothing.
 */
export const materialsExtractPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type MaterialsExtractPayload = z.infer<typeof materialsExtractPayloadSchema>;

/**
 * The answer format's shape for a reading of `asked`, as the provider is asked to hold it
 * (`materialsAnswerWire`): one key per fact asked for, each text or `null`, and the title, with no
 * bound. Built per reading, since which facts are asked depends on the study and its inputs; the
 * parser holds every bound and reads exactly the facts asked for.
 */
export const materialsAnswerWireShape = (asked: readonly FactId[]) =>
  z.strictObject({
    facts: z.strictObject(
      Object.fromEntries(asked.map((factId) => [factId, z.string().nullable()])),
    ),
    title: z.string().nullable(),
  });

/** The wire a reading of `asked` answers under. */
export const materialsAnswerWire = (asked: readonly FactId[]): AnswerWire =>
  answerWire('materials_answer', materialsAnswerWireShape(asked));

/**
 * The model's answer to `asked` as a JSON document: one entry per fact it was asked for, and a
 * title.
 *
 * Strict so there is no path from model output to a fact nobody asked for: an extra key, a missing
 * one, a value that is not text or `null`, blank text, or anything over its bound is not an
 * answer. A missing fact is `null` by contract, never a guess, which is what the instructions ask
 * for and what the parse holds the model to. Keyed by the asked ids alone, so every path a refusal
 * names is one of those ids or a key of the format, never a key the answer made up.
 */
const extractionAnswerSchema = (asked: readonly FactId[]) =>
  materialsAnswerWireShape(asked).extend({
    facts: z.strictObject(Object.fromEntries(asked.map((factId) => [factId, factValueSchema]))),
    title: words(MAX_PROPOSED_TITLE_CHARS).nullable(),
  });

/**
 * The rules a reading's answer can break, each the reason `parseMaterialsExtraction` refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a value that is blank or over its bound included;
 * - `facts`: not exactly the facts asked for.
 */
export const MATERIALS_ANSWER_RULES = ['document', 'shape', 'facts'] as const;

export type MaterialsAnswerRule = (typeof MATERIALS_ANSWER_RULES)[number];

/**
 * The facts and title a model's answer names, or the rule it broke when it is not a readable
 * answer to the facts asked for.
 *
 * The whole answer must be the JSON document, optionally inside one code fence, which is the one
 * wrapping a model adds on its own.
 */
export const parseMaterialsExtraction = (
  answer: string,
  asked: readonly FactId[],
): AnswerReading<
  { values: Record<FactId, string | null>; title: string | null },
  MaterialsAnswerRule
> => {
  const read = readAnswerDocument(unfencedAnswer(answer));
  if (read === null) {
    return { ok: false, rule: 'document' };
  }
  const document = read.value;

  // `reportInput` gives each issue the value it refused: none for a key the answer left out.
  const parsed = extractionAnswerSchema(asked).safeParse(document, { reportInput: true });
  if (!parsed.success) {
    const notAsked = parsed.error.issues.some(
      (issue) =>
        issue.path[0] === 'facts' &&
        ((issue.code === 'unrecognized_keys' && issue.path.length === 1) ||
          (issue.path.length === 2 && issue.input === undefined)),
    );

    return notAsked ? { ok: false, rule: 'facts', at: 'facts' } : refusedShape(parsed.error);
  }

  return { ok: true, value: { values: { ...parsed.data.facts }, title: parsed.data.title } };
};
